import { useMemo, useState, type ReactNode } from 'react';

import { CHAIN_IDS } from '@/constants/chain';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { tabsStyles } from '@/styles/tabs';
import { fontStyles } from '@/styles/typography';

import { Checkbox } from '@/ui/Checkbox';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import { Panel } from '@/ui/Panel';

import { classNames } from '@/utils/classnames';
import { getLimitOrderIdentityKey } from '@/utils/limit-order-identity';
import { tokenKey } from '@/utils/normalizeAddress';

import {
  type BuyTokenSelection,
  HistoryHeaderRow,
  historyGridCols,
  HistoryRowSkeleton,
} from './cells';
import { OpenOrderRow } from './OpenOrderRow';
import { TradeRow } from './TradeRow';
import {
  HistoryScopeToken,
  useCompactOpenOrders,
  useCompactOrderHistory,
} from './useHistorySectionData';

type HistoryTab = 'history' | 'open';

const TAB_LABELS: Record<HistoryTab, string> = {
  history: 'Order history',
  open: 'Open orders',
};

const TABS: HistoryTab[] = ['history', 'open'];

const ROW_LIMIT = 3;

const arrowStyles = buttons({
  size: 'small',
  shape: 'rounded',
  color: 'transparent',
  iconOnly: true,
  class: 'text-token-gray-1600 disabled:opacity-40 disabled:cursor-default',
});

type HistorySectionProps = {
  /**
   * Scopes own history + open orders to the charted token. Undefined while the
   * module's pair is still resolving (`scopeReady` false).
   */
  scopeToken: HistoryScopeToken | undefined;
  scopeReady: boolean;
  /** Lets a row's token logo move that token into the trade module's buy field. */
  buySelection?: BuyTokenSelection;
  embedded?: boolean;
  className?: string;
};

const EmptyState = ({ title, children }: { title: string; children: ReactNode }) => (
  // 184px matches the skeleton block: an `h-10` header plus three `h-12` rows.
  <div className="col-span-full flex min-h-[184px] flex-col items-center justify-center gap-1 px-2 py-6 text-center">
    <span className={fontStyles({ variant: 'LABEL_LG' })}>{title}</span>
    <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-700' })}>
      {children}
    </span>
  </div>
);

export const HistorySection = ({
  scopeToken,
  scopeReady,
  buySelection,
  embedded = false,
  className,
}: HistorySectionProps) => {
  const { ethereumWallet, solanaWallet, restoringWalletAddresses } = useMatchaWallets();
  const [checkoutMode] = useCheckoutMode();
  const [, setAdvancedOrderType] = useAdvancedOrderType();
  const [userTab, setUserTab] = useState<HistoryTab | null>(null);
  // Both tabs share this filter, so it survives tab and active-token changes.
  const [onlyThisToken, setOnlyThisToken] = useState(false);
  const [paging, setPaging] = useState({ scope: '', page: 1 });

  const activeToken = useMemo(() => (scopeToken ? [scopeToken] : undefined), [scopeToken]);
  // Unfiltered queries stay wallet-wide; the active token still picks the wallet.
  const historyScope = onlyThisToken ? activeToken : undefined;

  const isSolanaScope = activeToken?.[0]?.chainId === CHAIN_IDS.SOLANA;
  const address = isSolanaScope ? solanaWallet?.address : ethereumWallet?.address;
  const isRestoringWallet =
    address === undefined && !!restoringWalletAddresses?.[isSolanaScope ? 'solana' : 'ethereum'];

  // The page belongs to one wallet and token filter; either changing from
  // outside the component discards it.
  const scope = `${address ?? ''}|${onlyThisToken && scopeToken ? tokenKey(scopeToken.address, scopeToken.chainId) : ''}`;
  if (paging.scope !== scope) {
    setPaging({ scope, page: 1 });
  }
  const page = paging.scope === scope ? paging.page : 1;
  const setPage = (nextPage: number) => setPaging({ scope, page: nextPage });

  const own = useCompactOrderHistory({
    address,
    scopeTokens: historyScope,
    scopeReady,
    limit: ROW_LIMIT,
    page,
  });
  const openOrdersEnabled = !!address && scopeReady;
  const openOrders = useCompactOpenOrders({
    address,
    scopeTokens: historyScope,
    page,
    limit: ROW_LIMIT,
    enabled: openOrdersEnabled,
  });
  const hasOpenOrders = openOrders.records.length > 0;
  const hasOwnRows = own.rows.length > 0;
  // Local trades can fill the tab while the query loads, so a skeleton only
  // replaces an otherwise empty tab.
  const isHistoryLoading = (isRestoringWallet || own.isLoading) && !hasOwnRows;
  const isOpenLoading = (isRestoringWallet || openOrders.isLoading) && !hasOpenOrders;

  const tab =
    userTab ?? (checkoutMode === CheckoutModes.advanced && hasOpenOrders ? 'open' : 'history');
  const isLoading = tab === 'history' ? isHistoryLoading : isOpenLoading;
  const isError = tab === 'history' ? own.isError : openOrders.isError;
  const Container = embedded ? 'div' : Panel;

  // Paging pins the displayed tab, so loading or failing on a later page can't
  // fall back to the automatic default.
  const goToPage = (nextPage: number) => {
    setUserTab(tab);
    setPage(nextPage);
  };

  const openPageCount = Math.max(1, Math.ceil(openOrders.total / ROW_LIMIT));
  // Cancelling the last order on a page leaves it empty; step back to a real page.
  // A settled empty result lands on page 1, but a pending or failed fetch keeps
  // the requested page so its retry targets the same page.
  const openOrdersSettled = openOrdersEnabled && !openOrders.isLoading && !openOrders.isError;
  if (tab === 'open' && openOrdersSettled && page > openPageCount) {
    setPage(openPageCount);
  }

  const rowCount = tab === 'history' ? own.rows.length : openOrders.records.length;
  const firstRow = (page - 1) * ROW_LIMIT + 1;
  const hasNext = tab === 'history' ? own.hasNext : page * ROW_LIMIT < openOrders.total;

  if ((!address && !isRestoringWallet) || !scopeReady) {
    return null;
  }

  const scopeLabel = onlyThisToken ? scopeToken?.symbol : undefined;

  return (
    <Container className={classNames('flex flex-col p-3 gap-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mt-1">
        <div
          role="group"
          aria-label="History section"
          className={tabsStyles({ variant: 'card' }).container()}
        >
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              // Matches the trade module's tabs: sizing only, so the variant keeps
              // owning the active/inactive colours.
              className={tabsStyles({ variant: 'card', active: key === tab }).tab({
                class: 'text-[16px] font-medium',
              })}
              aria-pressed={key === tab}
              onClick={() => {
                setUserTab(key);
                setPage(1);
              }}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </div>

        {scopeToken && (
          <label
            className={fontStyles({
              variant: 'LABEL_SM',
              class: [
                'flex items-center gap-2 cursor-pointer whitespace-nowrap',
                'text-token-gray-800',
              ],
            })}
          >
            <Checkbox
              checked={onlyThisToken ? 'checked' : 'unchecked'}
              // Pin the tab so a filter that empties Open orders keeps it visible.
              onCheckedChange={() => {
                setUserTab(tab);
                setOnlyThisToken(!onlyThisToken);
              }}
            />
            Only this token
          </label>
        )}
      </div>

      <div className="overflow-x-auto no-scrollbar min-w-0">
        <div
          aria-busy={isLoading}
          className={classNames(
            // `w-max` keeps the row from collapsing while the wrapper scrolls on
            // narrow screens; from `desktop:` up the table stays inside the panel.
            'grid w-max min-w-full desktop:w-full justify-between gap-x-2 tablet:gap-x-3',
            historyGridCols(tab === 'open'),
          )}
        >
          {(isLoading || (tab === 'history' ? hasOwnRows : hasOpenOrders)) && (
            <HistoryHeaderRow
              dateLabel={tab === 'open' ? 'Expires' : 'Date'}
              showActions={tab === 'open'}
            />
          )}

          {isLoading &&
            Array.from({ length: ROW_LIMIT }).map((_, index) => <HistoryRowSkeleton key={index} />)}

          {tab === 'history' &&
            own.rows.map((entry) => (
              <TradeRow
                key={`${entry.chainId}-${entry.orderId ?? entry.transactionHash}`}
                entry={entry}
                buySelection={buySelection}
              />
            ))}

          {tab === 'open' &&
            openOrders.records.map((item) => (
              <OpenOrderRow
                key={getLimitOrderIdentityKey(item)}
                item={item}
                buySelection={buySelection}
              />
            ))}

          {isError && !isLoading && rowCount === 0 && (
            <EmptyState title="Couldn't load this page">
              <button
                type="button"
                className={linkStyles({ underline: true, color: 'purple' })}
                onClick={() => (tab === 'history' ? own.refetch() : openOrders.refetch())}
              >
                Try again
              </button>
            </EmptyState>
          )}

          {tab === 'history' && !hasOwnRows && !isHistoryLoading && !isError && (
            <EmptyState title={scopeLabel ? `No ${scopeLabel} order history` : 'No order history'}>
              {scopeLabel
                ? 'Trades for this token will show up here.'
                : 'Your trades will show up here.'}
            </EmptyState>
          )}

          {tab === 'open' && !hasOpenOrders && !isOpenLoading && !isError && (
            <EmptyState title={scopeLabel ? `No open ${scopeLabel} orders` : 'No open orders'}>
              Place a{' '}
              <button
                type="button"
                className={linkStyles({ underline: true, color: 'purple' })}
                onClick={() => setAdvancedOrderType('limit')}
              >
                limit
              </button>{' '}
              or{' '}
              <button
                type="button"
                className={linkStyles({ underline: true, color: 'purple' })}
                onClick={() => setAdvancedOrderType('stop-loss')}
              >
                advanced
              </button>{' '}
              order and it will appear here.
            </EmptyState>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 -my-1">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous page"
            disabled={page === 1 || isLoading}
            onClick={() => goToPage(page - 1)}
            className={arrowStyles}
          >
            <ChevronDownIcon className="rotate-90" />
          </button>
          <button
            type="button"
            aria-label="Next page"
            disabled={!hasNext || isLoading}
            onClick={() => goToPage(page + 1)}
            className={arrowStyles}
          >
            <ChevronDownIcon className="-rotate-90" />
          </button>
        </div>

        {rowCount > 0 && (
          <span
            className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}
            aria-live="polite"
          >
            {tab === 'open'
              ? `${firstRow}–${firstRow + rowCount - 1} of ${openOrders.total}`
              : `${firstRow}–${firstRow + rowCount - 1}`}
          </span>
        )}
      </div>
    </Container>
  );
};
